# This is a new file
<template>
  <div data-stk-theme="dark" class="advisor-core-form-upload">
    <!-- Global Loading Overlay -->
    <LoadingOverlay
      v-if="isLoading"
      :message="loadingMessage"
    />

    <div class="form-upload-container">
      <!-- Left Sidebar with Steps -->
      <div class="form-steps-sidebar">
        <!-- Step 1: Client Selection -->
        <div class="form-step">
          <Title size="7" class="step-title">
            <span class="step-indicator">Step 1:</span> Select Client
          </Title>
          <TypeaheadClientSearch
            v-model="selectedClientId"
            :selectedClientId="selectedClientId"
            :onSelect="handleClientChange"
            placeholder="Search for a client..."
          />
                    <!-- Specify who the form is for/about -->
                    <div v-if="selectedClientId" class="mt-4">
            <Field>
              <Label>Form Participants</Label>
              <div class="info-message mb-3">
                <p><i class="fas fa-info-circle"></i> Specify who this form is for. The selected client is added by default, but you can add additional people for joint forms. You can also edit the role field to be more specific-- "Client" is default.</p>
              </div>

              <div v-for="(participant, index) in participants" :key="index" class="participant-row mb-2">
                <div class="participant-fields">
                  <input 
                    type="text" 
                    v-model="participant.name" 
                    placeholder="Name"
                    class="input participant-name"
                  >
                  <input 
                    type="text" 
                    v-model="participant.role" 
                    placeholder="Role"
                    class="input participant-role"
                  >
                  <Button 
                    v-if="index > 0" 
                    variant="danger" 
                    iconOnly="fas fa-times"
                    @click="removeParticipant(index)"
                    class="remove-participant"
                  />
                </div>
              </div>

              <Button 
                variant="secondary" 
                @click="addParticipant"
                class="mt-2"
              >
                <i class="fas fa-plus mr-2"></i> Add Participant
              </Button>
            </Field>
          </div>

          <!-- Add checkboxes for dossier and documents -->
          <div v-if="selectedClientId" class="mt-4">
            <Field>
              <Label>Select Data Sources</Label>
              <div v-if="hasDossier" class="info-message mb-3">
                <p><i class="fas fa-info-circle"></i> We recommend using dossier data when available as it provides the most accurate and up-to-date information. More advanced users can also use document data directly if needed.</p>
              </div>
              <div class="checkbox-group">
                <label class="checkbox-option" :class="{ 'disabled': !hasDossier && hasDocuments }">
                  <input
                    type="checkbox"
                    v-model="useDossier"
                    :disabled="!hasDossier && hasDocuments"
                  >
                  Use Dossier Data
                  <span v-if="!hasDossier" class="unavailable-text">(Not Available)</span>
                </label>
                <label class="checkbox-option" :class="{ 'disabled': hasDossier && !hasDocuments }">
                  <input
                    type="checkbox"
                    v-model="useDocuments"
                    :disabled="hasDossier && !hasDocuments"
                  >
                  Use Document Data
                  <span v-if="!hasDocuments" class="unavailable-text">(Not Available)</span>
                </label>
              </div>
            </Field>
          </div>
        </div>

        <!-- Step 2: Entity Management -->
        <div v-if="shouldShowEntityReview" class="form-step" :class="{ 'disabled': !selectedClientId }">
          <Title size="7" class="step-title">
            <span class="step-indicator">Step {{ entityReviewStepNumber }}:</span> Review Entities
          </Title>

          <div v-if="!selectedClientId" class="info-message">
            <p><i class="fas fa-info-circle"></i> Please select a client first</p>
          </div>

          <div v-else-if="!useDocuments" class="info-message">
            <p><i class="fas fa-info-circle"></i> Document review is not needed since documents are not selected</p>
          </div>

          <div v-else>
            <EntityManagementPanel
              :entities="store.entities"
              :fields-for-entity="store.fieldsForEntity"
              :section-entity-mapping="store.sectionEntityMapping"
              @entity-merged="handleEntityMerge"
            />

            <Stack class="mt-4">
              <div class="info-message" v-if="!hasReviewedEntities">
                <p><i class="fas fa-info-circle"></i> Please review and merge any duplicate entities before proceeding</p>
              </div>
              
              <Button
                variant="primary"
                @click="completeEntityReview"
                class="mt-2"
              >
                <span>Continue to Form Upload</span>
                <i class="fas fa-arrow-right ml-2"></i>
              </Button>
            </Stack>
          </div>
        </div>

        <!-- Step 3: Form Upload -->
        <div class="form-step" :class="{ 'disabled': !selectedClientId || (useDocuments && !hasReviewedEntities) }">
          <Title size="7" class="step-title">
            <span class="step-indicator">Step {{ formUploadStepNumber }}:</span> Upload Form
          </Title>

          <div v-if="!selectedClientId" class="info-message">
            <p><i class="fas fa-info-circle"></i> Please select a client first</p>
          </div>

          <div v-else-if="useDocuments && !hasReviewedEntities" class="info-message">
            <p><i class="fas fa-info-circle"></i> Please review entities before uploading a form</p>
          </div>

          <div v-else>
            <Pills>
              <PillsList>
                <PillItem
                  :active="true"
                >
                  Direct PDF
                </PillItem>
              </PillsList>
            </Pills>

            <!-- Direct PDF Processing Section -->
            <Stack class="mt-4">
              <Field>
                <Label>Form Source</Label>
                <div class="radio-group">
                  <label class="radio-option">
                    <input
                      type="radio"
                      :value="true"
                      v-model="showExistingFormsTab"
                    >
                    Use Previously Uploaded Form
                  </label>
                  <label class="radio-option">
                    <input
                      type="radio"
                      :value="false"
                      v-model="showExistingFormsTab"
                    >
                    Upload New Form
                  </label>
                </div>
              </Field>

              <Field v-if="uploadedForms.length > 0 && showExistingFormsTab">
                <Label>Previously Uploaded Forms</Label>
                <select v-model="selectedUploadedFormId" class="form-select">
                  <option value="">Select a previously uploaded form</option>
                  <option v-for="form in uploadedForms" :key="form.external_id" :value="form.external_id">
                    {{ form.name || form.filename || 'Unnamed Form' }} ({{ formatDate(form.created_at) }})
                  </option>
                </select>
              </Field>

              <Field v-if="!showExistingFormsTab || uploadedForms.length === 0">
                <Label>Upload PDF Form</Label>
                <FileInput
                  accept=".pdf"
                  @change="handleFileUpload"
                  :disabled="!selectedClientId"
                />
                <p class="help-text">Only PDF files are supported</p>
                <div v-if="selectedPDF" class="mt-4">
                  <div class="box">
                    <div class="content">
                      <p class="has-text-weight-bold">Selected PDF: {{ selectedPDF.name }}</p>
                      <p class="is-size-7">Size: {{ formatFileSize(selectedPDF.size) }}</p>
                    </div>
                  </div>
                </div>
              </Field>

              <LoaderWithText
                v-if="isProcessingForm"
                class="mt-4"
                message="Uploading and processing PDF form..."
              />
            </Stack>
          </div>
        </div>

        <!-- Step 4: Form Processing -->
        <div class="form-step" :class="{ 'disabled': !selectedPDF }">
          <Title size="7" class="step-title">
            <span class="step-indicator">Step {{ formProcessingStepNumber }}:</span> Form Processing
          </Title>

          <div v-if="!selectedPDF" class="info-message">
            <p><i class="fas fa-info-circle"></i> Please upload or select a form first</p>
          </div>

          <div v-else class="processing-section">
            <p class="processing-info">
              After uploading your form, there are two ways to process it:
              <ul>
                <li><strong>Auto-Match:</strong> Let AI automatically match sections to entities and fill fields</li>
                <li><strong>Manual Matching:</strong> Manually link entities to sections in the right sidebar, then match fields</li>
              </ul>
            </p>

            <Button
              variant="primary"
              @click.stop="matchSectionsToEntities"
              :disabled="!canStartProcessing"
              :loading="isProcessingForm"
            >
              <span>{{ isProcessingForm ? 'Processing…' : 'Auto-Match & Fill Fields' }}</span>
            </Button>

            <div class="info-message mt-4">
              <p>
                <i class="fas fa-info-circle"></i> After processing, you can manually adjust section matches and field values using the controls in the right sidebar.
              </p>
            </div>

            <LoaderWithText 
              v-if="isMatchingFields"
              class="llm-matching-spinner"
              message="Using AI to match fields and transform data..."
            />
          </div>
        </div>

        <!-- Step 5: Download & Export -->
        <div class="form-step" :class="{ 'disabled': !uploadedPdfId || !hasCustomFieldValues }">
          <Title size="7" class="step-title">
            <span class="step-indicator">Step {{ downloadStepNumber }}:</span> Download & Export
          </Title>

          <div v-if="uploadedPdfId && !hasCustomFieldValues" class="info-message">
            <p><i class="fas fa-info-circle"></i> Form fields need to be filled before downloading</p>
          </div>

          <div v-if="!uploadedPdfId" class="info-message">
            <p><i class="fas fa-info-circle"></i> Complete steps 1-4 before downloading</p>
          </div>

          <div v-if="uploadedPdfId && hasCustomFieldValues" class="download-section">
            <p class="download-info">
              Your form is now filled and ready to download. You can also continue to make edits to any field values before finalizing.
            </p>

            <Buttons>
              <Button 
                variant="primary"
                @click="downloadFilledPdf"
                iconBefore="fas fa-download"
              >
                Download Filled PDF
              </Button>
            </Buttons>
          </div>
        </div>
      </div>

      <!-- PDF Viewer Section -->
      <div class="pdf-viewer-section">
        <div class="pdf-controls">
          <div class="page-navigation">
            <Button 
              @click="previousPage"
              :disabled="currentPdfPage === 1"
              iconOnly="fas fa-chevron-left"
            />
            
            <div class="page-input">
              <input
                v-model.number="currentPdfPage"
                type="number"
                min="1"
                :max="totalPages"
                @change="handleManualPageChange"
              />
              <span class="page-count">of {{ totalPages }}</span>
            </div>
            
            <Button 
              @click="nextPage"
              :disabled="currentPdfPage === totalPages"
              iconOnly="fas fa-chevron-right"
            />
          </div>
        </div>

        <div class="pdf-view-container">
          <PdfViewer
            ref="pdfViewer"
            :pdf-url="selectedPDF"
            :fields="mappedFields"
            :highlighted-section="store.highlightedSection"
            :section-colors="sectionColors"
            :scale="pdfScale"
            :currentPageProp="currentPdfPage"
            :getFieldGroup="(field) => field.context?.section || field.meta?.section || 'unknown'"
            @field-click="handleFieldClick"
            @pdf-loaded="handlePdfLoaded"
            @page-changed="handlePageChange"
            @error="handlePdfError"
          />
        </div>
      </div>

      <!-- Resize Handle -->
      <div 
        class="resize-handle"
        @mousedown="startResizing"
      />

      <!-- Section Legend Panel -->
      <SectionLegendPanel
        ref="sectionLegendPanel"
        class="section-legend-panel"
        :width="sectionPanelWidth"
        :sections="uniqueSections"
        :section-colors="sectionColors"
        :section-entity-mapping="store.sectionEntityMapping"
        :entities="store.entities"
        :highlighted-section="store.highlightedSection"
        :processing-sections="store.processingSections"
        @section-click="handleSectionClick"
        @entity-click="handleEntityClick"
        @navigate-to-source="handleNavigateToSource"
        @open-entity-dialog="handleOpenEntityDialog"
        @unlink-entity="handleUnlinkEntity"
      />
    </div>

    <!-- Entity Assignment Modal -->
    <div class="modal" :class="{ 'is-active': showEntityDialog }">
      <div class="modal-background" @click="showEntityDialog = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Link Entity to Section</p>
          <button class="delete" @click="showEntityDialog = false"></button>
        </header>
        <section class="modal-card-body">
          <div class="entity-list">
            <div 
              v-for="entity in store.filteredEntities"
              :key="entity.id"
              class="entity-item"
              @click="assignEntityToSection(entity.id)"
            >
              <div class="entity-info">
                <span class="entity-name">{{ entity.name }}</span>
                <span class="entity-type">{{ entity.type }}</span>
              </div>
              <span class="icon">
                <i class="fas fa-chevron-right"></i>
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
import LoadingOverlay from '../components/form-upload/LoadingOverlay.vue';
import EntityManagementPanel from '../components/EntityManagementPanel.vue';
import PdfViewer from '../components/PdfViewer.vue';
import {
  Button,
  Buttons,
  Field,
  FileInput,
  Label,
  Pills,
  PillsList,
  PillItem,
  Stack,
  Title,
  LoaderWithText,
} from '@stakeui';
import TypeaheadClientSearch from '@frontend/shared/components/TypeaheadClientSearch.vue';
import SectionLegendPanel from '../components/SectionLegendPanel.vue';
import { useFormDataStore } from '../store/formData';

export default {
  name: 'FormUploadView',

  components: {
    Button,
    Buttons,
    EntityManagementPanel,
    Field,
    FileInput,
    Label,
    LoadingOverlay,
    PdfViewer,
    Pills,
    PillsList,
    PillItem,
    SectionLegendPanel,
    Stack,
    Title,
    TypeaheadClientSearch,
    LoaderWithText,
  },

  data() {
    return {
      // Loading state
      loadingPdfDocument: false,
      isProcessingForm: false,
      isMatchingFields: false,
      loadingMessage: '',

      // PDF handling
      selectedPDF: null,
      pdfScale: 1.5,
      currentPdfPage: 1,
      totalPages: 0,
      pdfDoc: null,
      showFieldOverlays: true,

      // Client and entity management
      selectedClientId: '',
      hasReviewedEntities: false,
      temporaryEntityMerges: new Map(),
      participants: [], // who the form is about

      // UI state
      currentGroup: null,
      selectedField: null,
      sectionPanelWidth: 280,
      isResizingPanel: false,
      showEntityDialog: false,
      selectedSectionForEntity: null,

      // Form management
      uploadedFileName: '',
      uploadedPdfId: null,
      uploadedForms: [],
      selectedUploadedFormId: null,
      showExistingFormsTab: true,

      // Color palette for sections
      colorPalette: [
        '#4a90e2', // Blue
        '#50e3c2', // Teal
        '#b8e986', // Green
        '#f5a623', // Orange
        '#d0021b', // Red
        '#9013fe', // Purple
        '#ff33e9', // Pink
      ],
      sectionColorMap: new Map(),
    };
  },

  computed: {
    store() {
      return useFormDataStore();
    },

    useDossier: {
      get() {
        return this.store.useDossier;
      },
      set(value) {
        this.store.setUseDossier(value);
      }
    },

    useDocuments: {
      get() {
        return this.store.useDocuments;
      },
      set(value) {
        this.store.setUseDocuments(value);
      }
    },

    isLoading() {
      return this.loadingPdfDocument || this.isProcessingForm;
    },

    hasCustomFieldValues() {
      return Object.keys(this.store.fieldValues).length > 0;
    },

    uniqueSections() {
      if (!this.store.currentFormId) return [];
      return this.store.formSections;
    },

    mappedFields() {
      return this.store.annotations;
    },

    sectionColors() {
      const colors = {};
      this.uniqueSections.forEach(sectionId => {
        if (!this.sectionColorMap.has(sectionId)) {
          const nextColorIndex = this.sectionColorMap.size % this.colorPalette.length;
          this.sectionColorMap.set(sectionId, this.colorPalette[nextColorIndex]);
        }
        colors[sectionId] = this.sectionColorMap.get(sectionId);
      });
      return colors;
    },

    canStartProcessing() {
      return this.selectedClientId && this.selectedPDF && !this.isProcessingForm;
    },

    shouldShowEntityReview() {
      return this.selectedClientId && this.useDocuments;
    },

    entityReviewStepNumber() {
      // If we're not showing entity review, adjust step numbers
      return this.useDocuments ? 2 : 1;
    },

    formUploadStepNumber() {
      // If we're not using documents, form upload is step 2
      return this.useDocuments ? 3 : 2;
    },

    formProcessingStepNumber() {
      // If we're not using documents, form processing is step 3
      return this.useDocuments ? 4 : 3;
    },

    downloadStepNumber() {
      // If we're not using documents, download is step 4
      return this.useDocuments ? 5 : 4;
    },

    filteredEntities() {
      return this.store.filteredEntities(this.useDossier, this.useDocuments);
    }
  },

  watch: {
    selectedClientId(newId) {
      if (newId) {
        this.fetchEntities();
        this.fetchUploadedForms();
      }
    },

    selectedUploadedFormId(newId) {
      if (newId) {
        this.selectedPDF = `/api/v1/form-upload/${newId}/`;
        this.uploadedPdfId = newId;
        this.loadExistingForm(newId);
      }
    },

    'store.currentForm': {
      deep: true,
      handler(newForm) {
        this.$nextTick(() => {
          if (this.$refs.pdfViewer && typeof this.$refs.pdfViewer.updateFields === 'function') {
            this.$refs.pdfViewer.updateFields(newForm?.fields || []);
          }
        });
      },
    },

    'store.fieldValues': {
      handler(newValues) {
        console.log('Field values changed:', newValues);
        // Force a re-render of the PDF viewer when field values change
        this.$nextTick(() => {
          if (this.$refs.pdfViewer) {
            console.log('FormUploadView: Triggering PDF viewer re-render after field value change');
            // Update the fields array to reflect new values
            const updatedFields = this.mappedFields.map(field => ({
              ...field,
              value: newValues[field.id] ?? field.value,
            }));
            // Update the fields prop which will trigger a re-render
            this.$refs.pdfViewer.fields = updatedFields;
            // Then re-render all fields
            this.$refs.pdfViewer.renderFormFields();
          } else {
            console.warn('FormUploadView: PDF viewer ref not available after field value change');
          }
        });
      },
      deep: true,
    },

    currentPdfPage(newPage) {
      console.log(`FormUploadView: currentPdfPage changed to ${newPage}`);
    },

    useDossier(newValue) {
      if (this.selectedClientId) {
        this.fetchEntities();
      }
    },
    useDocuments(newValue) {
      if (this.selectedClientId) {
        this.fetchEntities();
      }
    },
  },

  mounted() {
    // Check for clientId in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('clientId');
    if (clientId) {
      // Fetch client data first
      fetch('/api/v1/docusign/doc-client-ids/')
        .then(response => response.json())
        .then(clients => {
          const selectedClient = clients.find(client => client.external_id === clientId);
          if (selectedClient) {
            this.handleClientChange(selectedClient);
          }
        })
        .catch(error => {
          console.error('Error fetching client data:', error);
          this.$oruga.notification.open({
            message: 'Error loading client data: ' + (error.message || 'Unknown error'),
            variant: 'danger',
          });
        });
    }

    this.fetchUploadedForms();
    window.addEventListener('mousemove', this.handlePanelResize);
    window.addEventListener('mouseup', this.stopResizing);
  },

  beforeDestroy() {
    window.removeEventListener('mousemove', this.handlePanelResize);
    window.removeEventListener('mouseup', this.stopResizing);
  },

  methods: {
    handleClientChange(client) {
      console.log('Client selected:', client);
      if (client) {
        this.selectedClientId = client.external_id;
        this.hasReviewedEntities = false; // Reset when client changes
        this.participants = [{
          name: client.name,
          role: 'Client',
          entityId: client.external_id
        }];
        this.resetForm();

        // Set dossier and documents availability based on client data
        this.hasDossier = client.attribute_count > 0;
        this.hasDocuments = client.doc_count > 0;

        // Set default values based on available data
        if (this.hasDossier && !this.hasDocuments) {
          this.store.setUseDossier(true);
          this.store.setUseDocuments(false);
        } else if (!this.hasDossier && this.hasDocuments) {
          this.store.setUseDossier(false);
          this.store.setUseDocuments(true);
        } else if (this.hasDossier && this.hasDocuments) {
          // If both are available, default to only dossier
          this.store.setUseDossier(true);
          this.store.setUseDocuments(false);
        }

        // Fetch entities with the current data source preference
        this.fetchEntities();
      }
      else{
        this.participants = []; // if no entity selected we have empty participants list
      }
    },

    addParticipant() {
      this.participants.push({
        name: '',
        role: '',
      });
    },
    
    removeParticipant(index) {
      this.participants.splice(index, 1);
    },
  
    // Client and Entity Management
    async fetchEntities() {
      try {
        const client_select_id = this.selectedClientId
        await this.store.loadEntities(this.selectedClientId);
        console.log('Client ID to fetch entities', client_select_id)
        // Reset entity review state when loading new entities
        this.hasReviewedEntities = false;
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error fetching entities:', error);
        this.$oruga.notification.open({
          message: 'Error loading entities: ' + (error.message || 'Unknown error'),
          variant: 'danger',
        });
      }
    },

    // Form Upload and Management
    async loadExistingForm(formId) {
      this.isProcessingForm = true;
      try {
        await this.fetchFormData(formId);
      } catch (error) {
        console.error('Error loading form:', error);
        this.$oruga.notification.open({
          message: 'Error loading form: ' + (error.message || 'Unknown error'),
          variant: 'danger',
        });
      } finally {
        this.isProcessingForm = false;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) {
        this.$oruga.notification.open({
          message: 'No file selected',
          variant: 'danger',
          duration: 3000,
        });
        return;
      }

      if (!file.type.includes('pdf')) {
        this.$oruga.notification.open({
          message: 'Please select a PDF file',
          variant: 'danger',
          duration: 3000,
        });
        return;
      }

      if (!this.selectedClientId) {
        this.$oruga.notification.open({
          message: 'Please select a client first',
          variant: 'danger',
          duration: 3000,
        });
        return;
      }

      this.selectedPDF = null; // Reset first
      this.isProcessingForm = true;
      this.loadingPdfDocument = true;

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('client_id', this.selectedClientId);

        // Upload the PDF and wait for processing to complete using awaitTask
        const result = await awaitTask('/api/v1/pdf-forms/upload/', {
          method: 'POST',
          body: formData,
          headers: { 'X-CSRFToken': this.$root.$data.csrfToken },
        });

        if (!result?.task) {
          throw new Error('No task information received from server');
        }

        if (result.task.status === 'complete' && result.task.meta) {
          const formId = result.task.meta.external_id;
          if (!formId) {
            throw new Error('No form ID returned from server');
          }

          // Set all necessary data before rendering
          this.uploadedPdfId = formId;
          this.currentPdfPage = 1;
          this.selectedPDF = `/api/v1/form-upload/${formId}/`;
          
          // Wait for next tick to ensure PDF viewer is ready
          await this.$nextTick();
          
          // Fetch form data after PDF viewer is ready
          await this.fetchFormData(formId);
          
          this.$oruga.notification.open({
            message: 'PDF uploaded and processed successfully!',
            variant: 'success',
            duration: 3000,
          });
        } else if (result.task.status === 'failed') {
          throw new Error(result.task.error || 'Form processing failed');
        } else {
          throw new Error('Form processing did not complete successfully');
        }
      } catch (error) {
        console.error('Error uploading PDF:', error);
        this.loadingPdfDocument = false;
        this.selectedPDF = null;
        this.$oruga.notification.open({
          message: 'Error uploading PDF: ' + (error.message || 'Unknown error'),
          variant: 'danger',
          duration: 3000,
        });
      } finally {
        this.isProcessingForm = false;
        this.loadingPdfDocument = false;
      }
    },

    async fetchFormData(formId) {
      try {
        this.isProcessingForm = true;
        console.log('FormUploadView: Fetching form data for ID:', formId);
        const formData = await this.store.loadFormData(formId);
        
        // Log the form data to debug
        console.log('FormUploadView: Loaded form data:', {
          formId,
          fieldCount: formData.form_fields?.length,
          fieldValues: this.store.fieldValues,
        });
        
        // The PDF viewer will automatically update through the :fields prop
        // which is bound to mappedFields computed property
      } catch (error) {
        console.error('Error fetching form data:', error);
        this.$oruga.notification.open({
          message: 'Error loading form: ' + (error.message || 'Unknown error'),
          variant: 'danger',
        });
        throw error;
      } finally {
        this.isProcessingForm = false;
      }
    },

    async fetchUploadedForms() {
      try {
        const response = await fetch('/api/v1/form-upload/');
        if (!response.ok) throw new Error('Failed to fetch uploaded forms');
        this.uploadedForms = await response.json();
      } catch (error) {
        console.error('Error fetching uploaded forms:', error);
      }
    },

    // PDF Viewer Controls
    handlePdfLoaded(pdf) {
      this.pdfDoc = pdf;
      this.totalPages = pdf.numPages;
      this.loadingPdfDocument = false;
      this.currentPdfPage = 1;
    },

    handlePdfError(error) {
      console.error('PDF Error:', error);
      this.loadingPdfDocument = false;
      this.$oruga.notification.open({
        message: `Error loading PDF: ${error.message}`,
        variant: 'danger',
      });
    },

    handlePageChange(newPage) {
      console.log(`FormUploadView: handlePageChange called with page ${newPage}`);
      this.currentPdfPage = newPage;
    },

    handleManualPageChange() {
      console.log(`FormUploadView: handleManualPageChange called with page ${this.currentPdfPage}`);
      this.handlePageChange(this.currentPdfPage);
    },

    previousPage() {
      if (this.currentPdfPage > 1) {
        console.log(`FormUploadView: Moving to previous page ${this.currentPdfPage - 1}`);
        this.handlePageChange(this.currentPdfPage - 1);
      }
    },

    nextPage() {
      if (this.currentPdfPage < this.totalPages) {
        console.log(`FormUploadView: Moving to next page ${this.currentPdfPage + 1}`);
        this.handlePageChange(this.currentPdfPage + 1);
      }
    },

    // Field Management
    handleFieldClick(field) {
      if (!field) return;
      
      // Log the exact structure of what we receive
      console.log('handleFieldClick received:', {
        field,
        fieldType: typeof field,
        fieldKeys: Object.keys(field),
        fieldString: JSON.stringify(field, null, 2)
      });

      // Extract the actual field data if it's nested
      const actualField = field.field || field;
      
      this.selectedField = actualField;
      const section = actualField?.context?.section || actualField?.meta?.section;
      if (section) {
        this.store.setHighlightedSection(section);
      }

      // Get the field identifier that matches the data-field-id attribute
      const fieldId = actualField.id || actualField.name || actualField.context?.id;
      console.log('Field identifier:', { fieldId, actualField });

      // Scroll to the field in the SectionLegendPanel
      this.$nextTick(() => {
        if (this.$refs.sectionLegendPanel && fieldId) {
          this.$refs.sectionLegendPanel.scrollToField(fieldId);
        } else {
          console.warn('Could not scroll to field:', { fieldId, actualField });
        }
      });
    },

    updateFieldValue({ field, value }) {
      console.log('updateFieldValue: Updating field value:', {
        fieldId: field.id,
        oldValue: field.value,
        newValue: value,
      });

      // Update the value in the store
      this.store.setFieldValue({ fieldId: field.id, value });
    },

    // Section Management
    handleSectionClick(section) {
      this.store.setHighlightedSection(section);
    },

    // Panel Resizing
    startResizing() {
      this.isResizingPanel = true;
    },

    handlePanelResize(event) {
      if (!this.isResizingPanel) return;
      
      const container = this.$el.querySelector('.pdf-view-container');
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const newWidth = Math.max(200, Math.min(event.clientX - rect.left, rect.width * 0.7));
      this.sectionPanelWidth = newWidth;
    },

    stopResizing() {
      this.isResizingPanel = false;
    },

    // Utility Functions
    resetForm() {
      this.selectedPDF = null;
      this.uploadedPdfId = null;
      this.currentPdfPage = 1;
      this.totalPages = 0;
      this.selectedField = null;
      this.store.clearFormData();
    },

    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch (error) {
        return dateString;
      }
    },

    handleEntityClick(entityId) {
      const entity = this.store.entities[entityId];
      if (entity) {
        this.selectedEntity = entity;
        this.showEntityDialog = true;
      }
    },

    startFormProcessing() {
      // This method is now empty as the form processing is handled automatically by the upload endpoint
    },

    // Field Management
    handleNavigateToSource(field) {
      if (!field) return;
      
      // Log the exact structure of what we receive
      console.log('handleNavigateToSource received:', {
        field,
        fieldType: typeof field,
        fieldKeys: Object.keys(field),
        fieldString: JSON.stringify(field, null, 2)
      });

      // Extract the actual field data if it's nested
      const actualField = field.field || field;
      
      this.selectedField = actualField;
      const section = actualField?.context?.section || actualField?.meta?.section;
      if (section) {
        this.store.setHighlightedSection(section);
      }

      // Get the field identifier that matches the data-field-id attribute
      const fieldId = actualField.id || actualField.name || actualField.context?.id;
      console.log('Field identifier:', { fieldId, actualField });

      // Scroll to the field in the PDF viewer
      this.$nextTick(() => {
        if (this.$refs.pdfViewer && fieldId) {
          this.$refs.pdfViewer.highlightField(actualField);
        } else {
          console.warn('Could not scroll to field:', { fieldId, actualField });
        }
      });
    },

    handleOpenEntityDialog(sectionId) {
      this.selectedSectionForEntity = sectionId;
      this.showEntityDialog = true;
    },

    async assignEntityToSection(entityId) {
      if (!this.selectedSectionForEntity) return;
      
      // Close dialog immediately
      this.showEntityDialog = false;
      const sectionId = this.selectedSectionForEntity;
      this.selectedSectionForEntity = null;

      try {
        // The store's linkEntityToSection method already handles setting processingSections
        const names = this.participants
        await this.store.linkEntityToSection({
          sectionId,
          entityId,
          names,
        });
        
        this.$oruga.notification.open({
          message: 'Entity linked successfully',
          variant: 'success',
        });
      } catch (error) {
        this.$oruga.notification.open({
          message: 'Failed to link entity: ' + error.message,
          variant: 'danger',
        });
      }
    },

    async handleUnlinkEntity(sectionId) {
      try {
        await this.store.unlinkEntityFromSection(sectionId);
        
        this.$oruga.notification.open({
          message: 'Entity unlinked successfully',
          variant: 'success',
        });
      } catch (error) {
        this.$oruga.notification.open({
          message: 'Failed to unlink entity: ' + error.message,
          variant: 'danger',
        });
      }
    },

    async downloadFilledPdf() {
      if (!this.store.currentFormId) {
        this.$oruga.notification.open({
          message: 'Please upload or select a form first',
          variant: 'danger',
        });
        return;
      }

      try {
        // Get the field information from annotations
        const fieldInfo = this.store.annotations.reduce((acc, field) => {
          acc[field.id] = {
            name: field.name || field.id,
            page: field.page || 1
          };
          return acc;
        }, {});

        // Call the fill endpoint which will return an S3 URL
        const response = await fetch('/api/v1/pdf-forms/fill/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            file_id: this.store.currentFormId,
            field_values: Object.entries(this.store.fieldValues).map(([id, value]) => ({
              id,
              name: fieldInfo[id]?.name || id, // Use the original field name from annotations
              value,
              page: fieldInfo[id]?.page || 1,
            })),
          }),
        });

        if (!response.ok) throw new Error('Failed to generate filled PDF');
        
        const data = await response.json();
        
        // Open the S3 URL in a new tab
        window.open(data.url, '_blank');

        this.$oruga.notification.open({
          message: 'Download started!',
          variant: 'success',
        });
      } catch (error) {
        console.error('Error initiating PDF download:', error);
        this.$oruga.notification.open({
          message: 'Error downloading PDF: ' + (error.message || 'Unknown error'),
          variant: 'danger',
        });
      }
    },

    handleEntityMerge({ sourceEntity, targetEntity }) {
      // Update entities list after merge
      this.store.setEntities(
        Object.values(this.store.entities).filter(e => e.id !== sourceEntity.id)
      );
      
      // Update any section mappings
      Object.entries(this.store.sectionEntityMapping).forEach(([section, entityId]) => {
        if (entityId === sourceEntity.id) {
          this.store.mapSectionToEntity({ sectionId: section, entityId: targetEntity.id });
        }
      });

      // Mark entities as reviewed
      this.hasReviewedEntities = true;

      this.$buefy.toast.open({
        message: 'Entities merged successfully',
        type: 'is-success',
      });
    },

    completeEntityReview() {
      // Set entities as reviewed
      this.hasReviewedEntities = true;
      
      // Scroll the next section into view
      this.$nextTick(() => {
        const formUploadStep = this.$el.querySelector('.form-step:nth-child(3)');
        if (formUploadStep) {
          formUploadStep.scrollIntoView({ behavior: 'smooth' });
        }
      });

      this.$buefy.toast.open({
        message: 'Entity review completed. You can now upload your form.',
        type: 'is-success',
      });
    },

    async matchSectionsToEntities() {
      try {
        this.isProcessingForm = true;
        this.$buefy.toast.open({
          message: 'Matching sections to entities...',
          type: 'is-info',
        });

        // Get all unique sections from the store's annotations
        const sections = this.store.annotations.reduce((acc, field) => {
          const sectionId = field.context?.section || field.meta?.section;
          if (sectionId && !acc[sectionId]) {
            acc[sectionId] = {
              id: sectionId,
              name: sectionId,
              fields: []
            };
          }
          if (sectionId) {
            acc[sectionId].fields.push({
              id: field.id,
              label: field.label || field.name,
              value: field.value || '',
              description: field.context?.descriptive_key || '',
              instructions: field.context?.instructions || '',
              checkbox_options: field.states || 'N/A'
            });
          }
          return acc;
        }, {});

        // Convert sections object to array
        const sectionsArray = Object.values(sections);

        const entities = this.store.filteredEntities;

        const selectedClientID =this.selectedClientId;

        const formId = this.uploadedPdfId;
        let entityFields = {}
        for(var entity of entities)
        {
          entityFields[entity.id] = this.store.entityFields[entity.id] || [];
        }

        // Call the match-sections-to-entities endpoint
        const result = await awaitTask('/api/v1/pdf-forms/match-sections-to-entities/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]').value,
          },
          body: JSON.stringify({
            form_id: formId,
            sections: sectionsArray,
            entities: entities.map(entity => entity.id),
            entity_fields: entityFields,
            names: this.participants
          }),
        });

        if (!result?.task?.meta?.section_entity_mapping?.mappings) {
          throw new Error('No section-entity mappings received from server');
        }

        // Process each section-entity mapping
        const mappings = result.task.meta.section_entity_mapping.mappings;
        for (const mapping of mappings) {
          const entityId = mapping.entity_id;
          const isValidEntityId = entityId && 
                                (typeof entityId === 'string' && entityId.trim() !== '') || 
                                (Array.isArray(entityId) && entityId.length > 0);
          
          if (isValidEntityId) {
            // Only call linkEntityToSection if we have a valid entity ID, otherwise will hit bug
            await this.store.linkEntityToSection({
              // entity_external_id: selectedClientID,
              sectionId: mapping.section_id,
              entityId: mapping.entity_id, 
              names: this.participants
            });
          } else {
            console.log(`Skipping section ${mapping.section_id} due to empty entity_id`);
          }
        }

        // Show success message
        this.$buefy.toast.open({
          message: 'Sections and fields matched to entities successfully',
          type: 'is-success',
        });

        // Emit event to notify parent components
        this.$emit('sections-matched', {
          mappings,
          totalSections: sectionsArray.length,
          matchedSections: mappings.length
        });
      } catch (error) {
        console.error('Error matching sections to entities:', error);
        this.$buefy.toast.open({
          message: 'Error matching sections to entities',
          type: 'is-danger',
        });
      } finally {
        this.isProcessingForm = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.advisor-core-form-upload {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--stk-text-10);
  color: var(--stk-text-strong);
}

.participant-fields {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.participant-fields .input {
  padding: 0.5rem;
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  background: var(--stk-text-15);
  color: var(--stk-text-strong);
  font-family: inherit;
  font-size: inherit;
}

.participant-fields .input:focus {
  border-color: var(--stk-primary);
  outline: none;
}

.participant-name {
  flex: 2;
}

.participant-role {
  flex: 1;
}

.remove-participant {
  flex-shrink: 0;
}

.form-upload-container {
  flex: 1;
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
  overflow: hidden;
  background-color: var(--stk-text-10);
}

.form-steps-sidebar {
  width: 400px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;
}

.form-step {
  background: var(--stk-text-20);
  border-radius: var(--stk-border-radius);
  padding: 1.5rem;
  border: 1px solid var(--stk-border);

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.step-title {
  margin-bottom: 1.25rem;
  color: var(--stk-text-strong);

  .step-indicator {
    color: var(--stk-primary);
    font-weight: 600;
  }
}

.info-message {
  color: var(--stk-text-weak);
  font-size: 0.875rem;
  padding: 0.75rem;
  background: var(--stk-text-15);
  border-radius: var(--stk-border-radius);
  border: 1px solid var(--stk-border);

  i {
    margin-right: 0.5rem;
  }
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: var(--stk-text-strong);

  input[type="radio"] {
    margin: 0;
    accent-color: var(--stk-primary);
  }
}

.form-select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--stk-border);
  border-radius: var(--stk-border-radius);
  background: var(--stk-text-15);
  color: var(--stk-text-strong);

  &:focus {
    border-color: var(--stk-primary);
    outline: none;
  }

  option {
    background: var(--stk-text-15);
    color: var(--stk-text-strong);
  }
}

.help-text {
  font-size: 0.875rem;
  color: var(--stk-text-weak);
  margin-top: 0.5rem;
}

.processing-section {
  .processing-info {
    font-size: 0.875rem;
    margin-bottom: 1rem;
    color: var(--stk-text-strong);

    ul {
      margin: 0.75rem 0 0 1.25rem;

      li {
        margin-bottom: 0.5rem;
      }
    }
  }
}

.download-section {
  .download-info {
    font-size: 0.875rem;
    margin-bottom: 1rem;
    color: var(--stk-text-strong);
  }
}

.pdf-viewer-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--stk-text-20);
  border-radius: var(--stk-border-radius);
  overflow: hidden;
  min-width: 0;
  border: 1px solid var(--stk-border);
}

.pdf-controls {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--stk-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--stk-text-15);
}

.page-navigation {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.page-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  input {
    width: 60px;
    text-align: center;
    padding: 0.25rem;
    border: 1px solid var(--stk-border);
    border-radius: var(--stk-border-radius);
    background: var(--stk-text-15);
    color: var(--stk-text-strong);

    &:focus {
      border-color: var(--stk-primary);
      outline: none;
    }
  }

  .page-count {
    color: var(--stk-text-weak);
  }
}

.pdf-view-container {
  flex: 1;
  position: relative;
  overflow: auto;
  background: var(--stk-text-10);
}

.resize-handle {
  width: 4px;
  background: var(--stk-border);
  cursor: col-resize;
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background: var(--stk-primary-30);
  }
}

.section-legend-panel {
  width: 280px;
  flex-shrink: 0;
  background: var(--stk-text-15);
  border-left: 1px solid var(--stk-border);
  border-radius: 0 var(--stk-border-radius) var(--stk-border-radius) 0;
}

// Modal styles
.modal {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.85);
  align-items: center;
  justify-content: center;

  &.is-active {
    display: flex;
  }
}

.modal-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.modal-card {
  position: relative;
  background: var(--stk-text-20);
  border-radius: var(--stk-border-radius);
  max-width: 480px;
  width: 100%;
  margin: 1rem;
  border: 1px solid var(--stk-border);
}

.modal-card-head {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--stk-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--stk-text-20);
}

.modal-card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--stk-text-strong);
}

.delete {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--stk-text-weak);

  &:hover {
    color: var(--stk-text-strong);
  }
}

.modal-card-body {
  padding: 1.5rem;
  background: var(--stk-text-20);
}

.entity-list {
  .entity-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem;
    border-radius: var(--stk-border-radius);
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--stk-primary-30);
    }

    .entity-info {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      .entity-name {
        font-weight: 500;
        color: var(--stk-text-strong);
      }

      .entity-type {
        font-size: 0.875rem;
        color: var(--stk-text-weak);
      }
    }

    .icon {
      color: var(--stk-text-weak);
    }
  }
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: var(--stk-text-strong);

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  input[type="checkbox"] {
    margin: 0;
    accent-color: var(--stk-primary);

    &:disabled {
      cursor: not-allowed;
    }
  }

  .unavailable-text {
    color: var(--stk-text-weak);
    font-size: 0.875rem;
  }
}

@media (max-width: 1024px) {
  .form-upload-container {
    flex-direction: column;
  }

  .form-steps-sidebar {
    width: 100%;
  }

  .pdf-viewer-section {
    height: 600px;
  }

  .section-legend-panel {
    width: 100% !important;
    border-left: none;
    border-top: 1px solid var(--stk-border);
    border-radius: 0 0 var(--stk-border-radius) var(--stk-border-radius);
  }

  .resize-handle {
    display: none;
  }
}

@media (max-width: 768px) {
  .form-upload-container {
    padding: 1rem;
  }

  .pdf-viewer-section {
    height: 500px;
  }

  .section-legend-panel {
    width: 100% !important;
    border-left: none;
    border-top: 1px solid var(--stk-border);
  }

  .resize-handle {
    display: none;
  }
}
</style>